import {CssToTailwindTranslator} from 'css-to-tailwind-translator';
import {isEmpty, trim} from 'lodash';

import {genTailwindcss} from '../api/webComponents';
import {
  circleCompStylesEcho, convertInlineCss2Rem,
  frameCompStylesEcho,
  groupCompStylesEcho,
  pathCompStylesEcho,
  rectCompStylesEcho,
  textCompStylesEcho, uuid4,
} from './index';
import {ComponentItem, ConfigAST, DynamicCss} from '../store/ast/ast.types';
import {
  BD_BL_RADIUS, BD_BOTTOM_WIDTH,
  BD_BR_RADIUS, BD_LEFT_WIDTH,
  BD_RADIUS, BD_RIGHT_WIDTH, BD_TL_RADIUS, BD_TOP_WIDTH, BD_TR_RADIUS,
  BD_WIDTH, BG_COLOR,
  BOTTOM,
  BOX_SHADOW, BOX_SIZING, DISPLAY,
  FONT_SIZE,
  HEIGHT,
  LEFT,
  LETTER_SPACING,
  LINE_HEIGHT,
  MARGIN_BOTTOM,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  MARGIN_TOP,
  MAX_HEIGHT,
  MAX_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH,
  OL_COLOR,
  OL_OFFSET,
  OL_STYLE,
  OL_WIDTH,
  OPACITY,
  OUTLINE,
  PADDING_BOTTOM,
  PADDING_LEFT,
  PADDING_RIGHT,
  PADDING_TOP,
  RIGHT,
  ROTATE,
  TOP,
  TRANSFORM,
  WIDTH,
} from '../components/configComponents/styleDesignBoard/constant';
import {genDefaultDynamicCss} from '../constant/common';
import {parseTailwindValueDesign} from '../components/configComponents/styleDesignBoard/utils';
import {convertToCss} from "./convert/convertToCss";

/**
 * 小驼峰转连词符.
 *
 * @param camelStr - 小驼峰字符串.
 * @returns 连词符字符串.
 */
export function camelToKebab(camelStr: string) {
  return camelStr.replace(/[A-Z]/g, function (match) {
    return '-' + match.toLowerCase();
  });
}

/**
 * 连词符转小驼峰.
 *
 * @param kebabStr - 连词符字符串.
 * @returns 小驼峰字符串.
 */
export function kebabToCamel(kebabStr: string) {
  return kebabStr.replace(/-([a-z])/g, (_, letter) => {
    // console.log(match);
    return letter.toUpperCase();
  });
}

/**
 * 十六进制颜色值转rgba函数.
 *
 * @param hex - 十六进制颜色值.
 * @returns rgba函数.
 */
export function hexToRgba(hex: string) {
  // 定义正则表达式来匹配十六进制颜色值
  const hexRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})|([a-f\d])([a-f\d])([a-f\d])$/i;
  const match = hex.match(hexRegex);

  if (match) {
    // 处理短格式十六进制颜色值（如 #fff）
    if (match[4]) {
      const r = parseInt(match[4] + match[4], 16);
      const g = parseInt(match[5] + match[5], 16);
      const b = parseInt(match[6] + match[6], 16);
      return `rgba(${r},${g},${b},1)`;
    }
    // 处理长格式十六进制颜色值（如 #ffffff）
    const r = parseInt(match[1], 16);
    const g = parseInt(match[2], 16);
    const b = parseInt(match[3], 16);
    return `rgba(${r},${g},${b},1)`;
  }
  // 如果输入的不是有效的十六进制颜色值，返回 null
  return null;
}

/**
 * 获取当前rem值.
 */
export function getRemValue() {
  return window.getComputedStyle(document.documentElement).getPropertyValue('font-size');
}

/**
 * 获取当前tailwind的--spacing变量值.
 */
export function getTailwindSpacingValue() {
  return window.getComputedStyle(document.documentElement).getPropertyValue('--spacing') || '0.25rem';
}

/**
 * 将css值转换为tailwind值.
 *
 * @param key - css样式名.
 * @param value - css样式值.
 * @returns tailwind值.
 */
export function parseCssToTailwind(key: string, value: any): string {
  value = value + '';
  const conversionResult = CssToTailwindTranslator(`{${camelToKebab(key)}: ${trim(value)}}`);
  return _get(conversionResult, ['data', 0, 'resultVal'], '');
}

/**
 * 将css转换为rem
 * @param key
 * @param value
 */
export function parseCssToTailwindRem(key: string, value: any) {
  return parseCssToTailwind(key, `${convertInlineCss2Rem(value)}rem`)
}

/**
 * 将css（px）转换为rem
 * @param key
 * @param value
 */
export function parseCssPx2TailwindRem(key: string, value: number) {
  return parseCssToTailwind(key, `${convertInlineCss2Rem(value + 'px')}rem`)
}

/**
 * 解析dynamicCss中的tailwind值，转换为css值，设计态展示用.
 *
 * @remark
 * 此方法为设计态用，由于拖拽原因，这里需要使用px值.
 *
 * @param css - dynamicCss中的tailwind对象.
 * @returns 标准css对象.
 */
export function parseTailwindToCss(css: { [cssKey: string]: string }): { [cssKey: string]: string } {
  const style = {};

  for (let [key, value] of Object.entries(css)) {
    key = kebabToCamel(key);  // 连词符转小驼峰.

    const isNotValid: boolean = value === '' || value === undefined || value === null;

    // 空值略过.
    if (isNotValid) {
      continue;
    }

    let parsedValue = parseTailwindValueDesign(key, value);  // 解析tailwind值，设计态

    // 对box-sizing的特殊处理.
    if (key === BOX_SIZING) {
      parsedValue = `${parsedValue}-box`;
    }

    const size = [WIDTH, HEIGHT, MIN_WIDTH, MIN_HEIGHT, MAX_WIDTH, MAX_HEIGHT];
    const padding = [PADDING_TOP, PADDING_RIGHT, PADDING_BOTTOM, PADDING_LEFT];
    const margin = [MARGIN_TOP, MARGIN_RIGHT, MARGIN_BOTTOM, MARGIN_LEFT];
    const position = [TOP, RIGHT, BOTTOM, LEFT];

    const pureNumberRegExp = new RegExp(/^[-+]?\d+(\.\d+)?$/);
    if ([...size, ...padding, ...margin, ...position].includes(key) && value && !['unset'].includes(value) && pureNumberRegExp.test(parsedValue)) {
      const rem = parseFloat(getRemValue());
      parsedValue = `${parseFloat(parsedValue) * rem}`;
    }

    // 特殊处理display: none;的情况.
    if (key === DISPLAY && parsedValue === 'hidden') {
      parsedValue = 'none';
    }

    style[key] = pureNumberRegExp.test(parsedValue) ? (parsedValue + addUnitSuffix(key)) : parsedValue;

    if (key === BOX_SHADOW) {
      style[key] = style[key].replaceAll('_', ' ');
    }

    if (key === BG_COLOR) {
      const match = convertToCss([value]).match(/:\s*(.*?)\s*;/);
      style[key] = match ? match[1] : '';
    }

  }

  return style;
}

/**
 * 根据样式名返回相应的单位.
 *
 * @remark
 * 此方法为设计态用，由于拖拽原因，这里需要使用px值.
 *
 * @param key - 样式名.
 * @returns 单位.
 */
export function addUnitSuffix(key: string): string {
  let unit = '';

  const size = [WIDTH, HEIGHT, MIN_WIDTH, MIN_HEIGHT, MAX_WIDTH, MAX_HEIGHT];
  const padding = [PADDING_TOP, PADDING_RIGHT, PADDING_BOTTOM, PADDING_LEFT];
  const margin = [MARGIN_TOP, MARGIN_RIGHT, MARGIN_BOTTOM, MARGIN_LEFT];
  const position = [TOP, RIGHT, BOTTOM, LEFT];
  const borderWidth = [BD_WIDTH, BD_TOP_WIDTH, BD_RIGHT_WIDTH, BD_BOTTOM_WIDTH, BD_LEFT_WIDTH];
  const borderRadius = [BD_RADIUS, BD_TL_RADIUS, BD_TR_RADIUS, BD_BR_RADIUS, BD_BL_RADIUS];

  if ([...size, ...padding, ...margin, ...position, ...borderWidth, ...borderRadius, LETTER_SPACING].includes(key)) {
    unit = 'px';
  } else if ([FONT_SIZE, LINE_HEIGHT].includes(key)) {
    unit = 'rem';
  } else if ([ROTATE].includes(key)) {
    unit = 'deg';
  } else if ([OPACITY].includes(key)) {
    unit = '%';
  }

  return unit;
}

/**
 * 生成原子组件tailwind形式的class值.
 *
 * @param dynamicCss - 当前原子组件的样式ast数据.
 * @param [componentType] - 原子组件类型.
 * @returns tailwind格式的样式字符串.
 */
export function genAtomComponentClass(dynamicCss: DynamicCss[], componentType?: string): string {
  const defaultItem: DynamicCss = dynamicCss.find(({type, bind}) => type === 'default' && bind === 'default');
  const statusItem: DynamicCss = dynamicCss.find(({type, bind}) => type === 'status' && bind === 'status');

  const defaultSources = _get(defaultItem, 'sources', []);
  const statusSources = _get(statusItem, 'sources', []);

  const classes: string[] = [];

  // 默认样式.
  defaultSources.forEach(source => {
    const styles = _get(source, 'css.normal', {});

    for (let key in styles) {
      classes.push(styles[key]);
    }
  });

  // 状态样式.
  statusSources.forEach(source => {
    const styles = _get(source, 'css.variants', {});

    for (let key in styles) {
      classes.push(styles[key]);
    }
  });

  const newClasses = [...new Set(classes)];

  if (componentType === 'path') {
    const bgIndex = newClasses.findIndex(i => i.includes('bg-'));

    if (bgIndex !== -1) {
      newClasses.splice(bgIndex, 1);
    }
  }

  return newClasses.join(' ');
}

/**
 * 按下面的步骤，帮我用 ts 写一个函数，传入一个字符串
 * 1、判断是否含有 "[rgba" ，就直接返回 【"[rgab" + 前面的子串】，退出方法执行。例如："text-[rgba(247,232,4,1.00)]"   返回 "text-[rgba"
 * 2、判断是否含有 "[#" ，就直接返回 【"[#" + 前面的子串】，退出方法执行。例如："text-[#ffffff]"   返回 "text-[rgba"
 * 3、判断是否含有 "[" ，就直接返回 "["  前面的子串，退出方法执行。例如："text-[10rem]"   返回 "text-"
 * 4、如果不含"["，就判断是否含有 "数字"，如果含有 "数字"，就返回第一个"数字"前面的子串，退出方法执行。例如："left-26.5"  返回 "left"
 * 5、如果 "数字" 和 "[" 都不包含，就返回原值
 * @param str
 */
function extractPrefix(str: string): string {

  // 1. 判断是否含有 "text-[rgba" 和 "text-[#"

  if (str.startsWith('text-[rgba') || str.startsWith('text-[#')) {
    return "text-[rgba";
  }

  // 3. 判断是否含有 "["
  const bracketIndex = str.indexOf('[');
  if (bracketIndex !== -1) {
    return str.substring(0, bracketIndex);
  }

  // 4. 判断是否含有数字
  const digitMatch = str.match(/\d/);
  if (digitMatch && digitMatch.index !== undefined) {
    return str.substring(0, digitMatch.index);
  }

  // 5. 如果数字和[都不包含，返回原值
  return str;
}


/**
 * 在属性改变时根据设计态配置好的匹配规则生成dynamicCss.default.
 *
 * @param dynamicCss - 当前原子组件的样式ast数据.
 * @param attrName - 变更的属性名.
 * @param attrValue - 变更的属性值.
 * @param data - 组件ast.
 */
export function genRuntimeDynamicCss(dynamicCss: DynamicCss[], attrName: string, attrValue: any, data: any): DynamicCss[] {
  const defaultItem: DynamicCss = dynamicCss.find(({type, bind}) => type === 'default' && bind === 'default');
  const attrItem: DynamicCss = dynamicCss.find(({type, bind}) => type === 'prop' && bind === attrName);

  const attrSources = _get(attrItem, 'sources', []);

  // 将source.value转换为相应类型，因为source.value的类型为字符串.
  function parseType(value: string) {
    const type = _get(data, `attrs.${attrName}.type`);
    if (type === 'number') {
      return parseFloat(value);
    } else if (type === 'boolean') {
      if (value === 'false') {
        return false;
      }
      return Boolean(value);
    } else if (type === 'object') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    } else if (type === 'array') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return value;
      }
    } else {
      return value;
    }
  }

  const nonValue = [undefined, null, '', NaN, 'null'];

  const targetSource = attrSources.filter(source => {
    if (source.expression === 'eq' && parseType(source.value) === attrValue) {
      return true;
    } else if (source.expression === 'neq' && parseType(source.value) !== attrValue) {
      return true;
    } else if (source.expression === 'gt' && parseType(source.value) > attrValue) {
      return true;
    } else if (source.expression === 'gte' && parseType(source.value) >= attrValue) {
      return true;
    } else if (source.expression === 'lt' && parseType(source.value) < attrValue) {
      return true;
    } else if (source.expression === 'lte' && parseType(source.value) <= attrValue) {
      return true;
    } else if (source.expression === 'empty' && nonValue.includes(attrValue)) {
      return true;
    } else if (source.expression === 'non-empty' && !nonValue.includes(attrValue)) {
      return true;
    } else {
      return false;
    }
  });
  if (targetSource) {
    const defaultNormalCss = _cloneDeep(_get(defaultItem, 'sources.0.css.normal', {}));
    for (const targetSourceItem of targetSource) {
      const attrNormalCss = _cloneDeep(_get(targetSourceItem, 'css.normal', {}));
      if (targetSourceItem["action"] == "add") {
        for (const src_css of Object.values(attrNormalCss)) {
          defaultNormalCss[uuid4()] = src_css;
        }
      } else if (targetSourceItem["action"] == "del") {
        for (const src_css of Object.values(attrNormalCss)) {
          for (const [dkey, dvalue] of Object.entries(defaultNormalCss)) {
            if (dvalue === src_css) {
              delete defaultNormalCss[dkey];
            }
          }

          // 遍历样式对象，严格匹配 空格分隔的完整类名
          for (const [dkey, dvalue] of Object.entries(defaultNormalCss)) {
            // 1. 按空格拆分成数组，过滤掉要删除的类名
            // @ts-ignore
            const classList = dvalue.split(' ').filter(className => {
              // 只删除 完全相等 的类名
              return className !== src_css;
            });
            // 2. 重新拼接回字符串
            defaultNormalCss[dkey] = classList.join(' ').trim();
          }
        }
      } else {
        const temp_src_css = new Set()
        for (const key of Object.keys(attrNormalCss)) {
          // 处理键相同
          defaultNormalCss[key] = attrNormalCss[key];
          if (attrNormalCss[key].indexOf("-") != -1) {
            temp_src_css.add(attrNormalCss[key].split("-")[0])
          }
        }
        // 处理键不同
        const not_in_acss = {}
        for (const key of Object.keys(defaultNormalCss)) {
          if (!attrNormalCss[key]) {
            not_in_acss[key] = defaultNormalCss[key]
            delete defaultNormalCss[key];
          }
        }

        // 样式前缀相同的，只取 attrs 中的一个
        if (targetSourceItem["action"] == "cover") {  // 覆盖的特殊处理下
          // 处理值前缀相同 例如 text-[], text-warp, left-0, left-[
          // 截取到"["和"数字": 使用 "text-" 判断是否相同， 相同的，覆盖上去
          const newAttrNormalCssMap = {};
          for (const value of Object.values(attrNormalCss)) {
            if (typeof value === "string") {
              const reValue = extractPrefix(value);
              newAttrNormalCssMap[reValue] = value;
            }    // 这里截取 "[" 和 "数字" 之前的字符串
          }

          for (let [k, v] of Object.entries(defaultNormalCss)) {
            if (typeof v === "string") {
              const reValue = extractPrefix(v);
              // 这里截取 "[" 和 "数字" 之前的字符串
              if (newAttrNormalCssMap[reValue]) {
                defaultNormalCss[k] = newAttrNormalCssMap[reValue];
              }
            }
          }
        }


        const new_dcss = new Set()
        for (const dvalue of Object.values(not_in_acss)) {
          const dvsps = (dvalue as string).split(" ")
          for (const dvsp of dvsps) {
            if (dvsp.indexOf("-") != -1 && temp_src_css.has(dvsp.split("-")[0])) {

            } else {
              new_dcss.add(dvsp)
            }
          }
        }
        for (const newDcss of new_dcss) {
          defaultNormalCss[uuid4()] = newDcss
        }
      }
    }
    _set(defaultItem, 'sources.0.css.normal', defaultNormalCss);
  }

  return dynamicCss;
}

/**
 * 遍历AST，为每个原子组件生成dynamicCss对象.
 *
 * @param components - AST原子组件数组.
 */
export function genDynamicCssByCss(components: ComponentItem[]) {
  const rem: number = parseFloat(getRemValue());
  const varSpacing: number = parseFloat(getTailwindSpacingValue());
  const rate = rem * varSpacing;

  function recur(prop: string, pX: number, pY: number) {
    const component = components.find(component => component.prop === prop);

    if (!component) {
      return;
    }

    // 无css对象直接跳过.
    if (isEmpty(component.css)) {
      return;
    }

    // 有效值判断.
    const positionPath = 'dynamicCss.0.sources.0.css.normal.position';
    const position = _get(component, positionPath);
    if (position && position !== 'absolute') {
      return;
    }

    const dynamicCss: DynamicCss[] = genDefaultDynamicCss();

    const type = component.type;
    const css = component.css;

    // PP过来的节点默认position都是absolute.
    let styles = {position: 'absolute'};

    // 根据节点类型将PP格式的css对象转换成标准style.
    switch (type) {
      case 'frame':
        styles = {...styles, ...frameCompStylesEcho(css)};
        break;
      case 'group':
        styles = {...styles, ...groupCompStylesEcho(css)};
        break;
      case 'rectangle':
        styles = {...styles, ...rectCompStylesEcho(css)};
        break;
      case 'circle':
        styles = {...styles, ...circleCompStylesEcho(css)};
        break;
      case 'text':
        styles = {...styles, ...textCompStylesEcho(css)};
        break;
      case 'path':
        styles = {...styles, ...pathCompStylesEcho(css)};
        break;
    }

    // 默认基础样式对象.
    const defaultNormalCss = {};

    for (const [key, value] of Object.entries(styles)) {
      // 遍历黑名单，以下属性不参与生成.
      const blackList: string[] = [OUTLINE, OL_WIDTH, OL_STYLE, OL_COLOR, OL_OFFSET];

      if (blackList.includes(key)) {
        continue;
      }

      const conversionResult = CssToTailwindTranslator(`{${camelToKebab(key)}: ${trim(value)}}`);
      const tailwindStr = _get(conversionResult, ['data', 0, 'resultVal']);

      // 对旋转属性做特殊处理，将key值又transform改为rotate.
      _set(defaultNormalCss, camelToKebab(key === TRANSFORM ? ROTATE : key), tailwindStr);
    }

    const getSizeValue = (value: number = 0) => {
      // 判断值是否为0.5的倍数.
      const isHalfMultiple = (value / rate) % 0.5 === 0;

      // 0.5倍数的值符合tailwind间距规则.
      return isHalfMultiple ? `${value / rate}` : `[${value / rem}rem]`;
    };

    // 宽高坐标这四个属性没有用到解析方法，这里单独处理.
    _set(defaultNormalCss, 'top', `top-${getSizeValue(css['~:y'] - pY)}`);
    _set(defaultNormalCss, 'left', `left-${getSizeValue(css['~:x'] - pX)}`);
    _set(defaultNormalCss, 'width', `w-${getSizeValue(css['~:width'])}`);
    _set(defaultNormalCss, 'height', `h-${getSizeValue(css['~:height'])}`);

    const defaultDynamicCssPath = '0.sources.0.css.normal';
    _set(dynamicCss, defaultDynamicCssPath, defaultNormalCss);

    component.dynamicCss = dynamicCss;

    const children = _get(component, 'children', []);
    children.forEach((child) => {
      recur(child.prop, css['~:x'], css['~:y']);
    });
  }

  const rootChildren = _get(components, '0.children', []);
  rootChildren.forEach((rootChild) => {
    recur(rootChild.prop, 0, 0);
  });
}

/**
 * 生成tailwind样式.
 *
 * @param styles - 样式对象.
 */
export function genClassList(styles) {
  const classes: string[] = [];

  for (const [key, value] of Object.entries(styles)) {
    if (key === 'pointerEvents') {
      continue;
    }

    const conversionResult = CssToTailwindTranslator(`{${camelToKebab(key)}: ${trim(value)}`);

    const tailwindStr = _get(conversionResult, ['data', 0, 'resultVal']);

    if (tailwindStr) {
      classes.push(tailwindStr);
    }
  }

  return classes;
}

/**
 * 将tailwind生成的样式代码打到DOM的style标签上.
 *
 * @param styleCode - tailwind生成的样式代码.
 */
export function genStyleTag(styleCode: string) {
  // 只用一个style标签.
  let styleTag: HTMLStyleElement = document.querySelector('style[title="tailwindcss"]');

  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.title = 'tailwindcss';
    document.head.appendChild(styleTag);
  }

  styleTag.textContent += styleCode;
}

/**
 * 遍历AST生成tailwind样式代码.
 *
 * @param ast - 组件的AST数据.
 * @returns 生成的tailwind样式代码字符串.
 */
export async function genTailwindStyleCode(ast: ConfigAST): Promise<string> {
  const classes: Set<string> = new Set();

  const components = _get(ast, 'components', []);

  components.forEach(component => {
    const dynamicCss = _get(component, 'dynamicCss', []);

    dynamicCss.forEach(dynamicCssItem => {
      const sources = _get(dynamicCssItem, 'sources', []);

      sources.forEach(source => {
        const normalStyles = _get(source, 'css.normal', {});
        const variantsStyles = _get(source, 'css.variants', {});
        const responsiveStyles = _get(source, 'css.responsive', {});

        for (let key in normalStyles) {
          classes.add(normalStyles[key]);
        }

        for (let key in variantsStyles) {
          classes.add(variantsStyles[key]);
        }

        for (let key in responsiveStyles) {
          classes.add(responsiveStyles[key]);
        }
      });
    });
  });

  const tailWindClasses: string = [...classes].join(' ');

  let tailwindStyleCodes = '';
  try {
    const res = await genTailwindcss({tailWindClasses});
    tailwindStyleCodes = _get(res, ['data', 'items'], '')
  } catch (error) {
    console.info("genTailwindStyleCode Error", error)
  }
  return tailwindStyleCodes;
}
